import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../utils/supabaseClient';
import { useUser } from '../contexts/UserContext';

/**
 * Reusable SkillsPicker component
 * - Loads company skills catalog
 * - Supports optional level (1-5) and quantity controls
 * - Allows creating new skills inline when allowCreate=true
 *
 * value: Array<{ skill_id: string, name?: string, level?: number, quantity?: number }>
 * onChange: (next: value) => void
 */
export default function SkillsPicker({
  value = [],
  onChange,
  showLevel = true,
  showQuantity = false,
  allowCreate = true,
}) {
  const { user } = useUser();
  const companyId = user?.company_id;

  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newSkillName, setNewSkillName] = useState('');

  // Load catalog
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data: list, error } = await supabase
          .from('skills')
          .select('id,name')
          .eq('company_id', companyId)
          .eq('active', true)
          .order('name', { ascending: true });

        if (!cancelled && !error) {
          setSkills(list || []);
        }
      } catch (e) {
        console.warn('Skills load failed', e);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const options = useMemo(() => skills.map(s => ({ id: s.id, name: s.name })), [skills]);

  const addSkill = async (skillIdOrName) => {
    if (!skillIdOrName) return;

    // If a name that doesn't match existing, create it (when allowed)
    const existing = options.find(o => o.id === skillIdOrName || o.name.toLowerCase() === String(skillIdOrName).toLowerCase());
    let skillId = existing?.id;

    if (!skillId && allowCreate) {
      // Create new skill in catalog
      try {
        const body = { name: String(skillIdOrName).trim(), company_id: companyId };
        const { data: created, error } = await supabase
          .from('skills')
          .insert(body)
          .select()
          .single();

        if (created && !error) {
          setSkills(prev => [...prev, created]);
          skillId = created.id;
        }
      } catch (e) {
        console.error('Failed to create skill', e);
      }
    }

    if (!skillId) return;

    // Skip duplicates
    if (value.some(v => v.skill_id === skillId)) return;
    const next = [...value, { skill_id: skillId, name: existing?.name || newSkillName || '', level: showLevel ? 1 : undefined, quantity: showQuantity ? 1 : undefined }];
    onChange && onChange(next);
    setNewSkillName('');
  };

  const removeSkill = (skillId) => {
    const next = value.filter(v => v.skill_id !== skillId);
    onChange && onChange(next);
  };

  const setLevel = (skillId, lvl) => {
    const next = value.map(v => v.skill_id === skillId ? { ...v, level: Math.min(5, Math.max(1, Number(lvl) || 1)) } : v);
    onChange && onChange(next);
  };

  const setQuantity = (skillId, qty) => {
    const n = Math.max(1, Math.floor(Number(qty) || 1));
    const next = value.map(v => v.skill_id === skillId ? { ...v, quantity: n } : v);
    onChange && onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <select
          className="flex-1 bg-gray-800/50 border border-gray-600/50 rounded-lg px-3 py-2 text-white backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
          disabled={loading}
          value=""
          onChange={(e) => addSkill(e.target.value)}
        >
          {/* Board #448. These carried a background and NO foreground, so the OS drew its
              default black text on a dark popup and the options were unreadable. A native
              <option> inherits nothing useful from the <select>; both colours have to be
              stated on the option itself. */}
          <option value="" disabled className="bg-gray-800 text-gray-300">Select a skill...</option>
          {options.map(o => (
            <option key={o.id} value={o.id} className="bg-gray-800 text-white">{o.name}</option>
          ))}
        </select>
        {allowCreate && (
          <>
            <input
              className="flex-1 bg-gray-800/50 border border-gray-600/50 rounded-lg px-3 py-2 text-white placeholder-gray-400 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-primary-500/50"
              placeholder="Add new skill"
              value={newSkillName}
              onChange={(e)=>setNewSkillName(e.target.value)}
            />
            <button type="button" className="btn-secondary" onClick={()=>addSkill(newSkillName.trim())} disabled={!newSkillName.trim()}>Add</button>
          </>
        )}
      </div>

      {/* Selected list */}
      <div className="space-y-2">
        {value.length === 0 ? (
          <div className="text-xs text-gray-400">No skills selected</div>
        ) : value.map(v => {
          const displayName = v.name || options.find(o=>o.id===v.skill_id)?.name || v.skill_id;
          return (
            <div key={v.skill_id} className="flex items-center gap-3 bg-gray-800/30 border border-gray-600/30 rounded-lg p-3 backdrop-blur-sm">
              <div className="flex-1 text-sm text-white">{displayName}</div>
              <button type="button" className="btn-secondary" onClick={()=>removeSkill(v.skill_id)}>Remove</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

